/**
 * Format a `YYYY-MM-DD` game-date string for display without the
 * classic timezone-shift bug.
 *
 * Passing a date-only ISO string to `new Date()` (e.g.
 * `new Date('2026-11-01')`) parses as UTC midnight, so viewers in
 * negative-offset timezones (Americas) see the calendar day shift
 * back by one when they call `toLocaleDateString()`. This helper
 * parses the string manually and constructs a Date with LOCAL
 * midnight so the displayed calendar day always matches the string
 * the organizer entered.
 *
 * Accepts:
 *   - `YYYY-MM-DD`                          → local midnight of that day
 *   - Empty / null / undefined              → returns ''
 *   - Any other value (ISO 8601 w/ time)    → falls back to `new Date(...)`
 *
 * @param {string}   raw     A date string from the API (usually YYYY-MM-DD).
 * @param {object}   [opts]  toLocaleDateString options (defaults to
 *                           { year: 'numeric', month: 'short', day: 'numeric' }).
 * @returns {string} Localised, human-friendly date string.
 */
export function formatGameDate(raw, opts) {
  if (!raw) return '';
  const options = opts || { year: 'numeric', month: 'short', day: 'numeric' };
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(raw));
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString(undefined, options);
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return String(raw);
  return parsed.toLocaleDateString(undefined, options);
}
